/**
 * Cartao de um provedor de webhook (Kiwify, Hotmart, Shopify ou o generico):
 * URL copiavel, estado do segredo, ultimo evento aceito, teste passo a passo
 * e historico dos ultimos eventos.
 *
 * Decisoes nao obvias:
 *  - Provedor NAO conectado continua mostrando a URL: e justamente o que a
 *    pessoa precisa copiar para conectar. O que muda e o contorno tracejado,
 *    o titulo apagado e o botao de teste desabilitado -- nao a opacidade do
 *    cartao inteiro, que derrubaria o contraste do texto que ela precisa ler.
 *  - O historico vive em estado LOCAL: comeca com o que veio do servidor e
 *    cada teste prende um registro no topo com o selo "Teste". Assim a pessoa
 *    ve o teste no mesmo lugar em que veria o evento real.
 *  - `sequencia` sobe ao ABRIR o teste, nunca ao receber o resultado: subir no
 *    resultado reiniciaria o efeito do modal e o teste rodaria para sempre.
 *  - `extra` e o espaco do que so um provedor tem (o generico poe o cabecalho
 *    da assinatura, o exemplo de corpo e o contrato). Entra logo abaixo da
 *    URL, antes do segredo, porque e o que a pessoa precisa para conectar.
 *  - O texto do segredo explica o custo de nao ter: sem ele o motor responde
 *    401 e a falha e silenciosa na plataforma.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { Badge, Button, Icon, Panel } from '../../../components/ui';
import { formatDateTime } from '../../../lib/format';
import type { WebhookEventLog, WebhookIntegration } from '../types';
import { EVENT_LABEL, WEBHOOK_PROVIDER_INFO } from '../vocabulario';
import { CampoCopiavel } from './CampoCopiavel';
import { HistoricoDeEventos } from './HistoricoDeEventos';
import { TesteDeWebhook } from './TesteDeWebhook';

export function CartaoProvedor({ integracao, extra }: { readonly integracao: WebhookIntegration; readonly extra?: ReactNode }): JSX.Element {
  const [eventos, setEventos] = useState<readonly WebhookEventLog[]>(integracao.recentEvents);
  const [testando, setTestando] = useState(false);
  const [sequencia, setSequencia] = useState(0);
  const [ultimoTeste, setUltimoTeste] = useState<WebhookEventLog | null>(null);

  const { provider, connected, secretState, lastEventAt, lastEventType } = integracao;
  const info = WEBHOOK_PROVIDER_INFO[provider];
  const semSegredo = connected && secretState === 'missing';

  const abrirTeste = (): void => {
    setSequencia((s) => s + 1);
    setTestando(true);
  };

  const registrarResultado = useCallback((evento: WebhookEventLog): void => {
    setEventos((anteriores) => [evento, ...anteriores]);
    setUltimoTeste(evento);
  }, []);

  return (
    <Panel tone={connected ? 'surface' : 'elevated'} className={`flex flex-col gap-4 p-4 ${connected ? '' : 'border-dashed border-edge'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className={`text-base font-semibold ${connected ? 'text-ink' : 'text-ink-muted'}`}>{info.label}</h3>
          <p className="text-xs text-ink-subtle">{info.descricao}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral" icon="check">
            Pronto no motor
          </Badge>
          <Badge tone={connected ? (semSegredo ? 'warning' : 'good') : 'neutral'} dot>
            {connected ? (semSegredo ? 'Conectado, sem segredo' : 'Conectado') : 'Não conectado'}
          </Badge>
        </div>
      </div>

      <CampoCopiavel label="URL do webhook" value={integracao.webhookUrl} hint={info.hint} />

      {extra}

      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title="Prova de que o evento veio da plataforma, e não de qualquer pessoa que descobriu a URL.">
            Segredo de assinatura
          </dt>
          <dd className="mt-1 flex flex-col gap-1">
            {secretState === 'configured' ? (
              <Badge tone="good" icon="chave">
                Configurado
              </Badge>
            ) : (
              <Badge tone="warning" icon="chave">
                Não configurado
              </Badge>
            )}
            <span className="text-xs text-ink-subtle">
              {info.esquema} · {info.onde}
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title="Último evento que passou pela assinatura e entrou no motor. Rejeitados ficam só no histórico.">
            Último evento aceito
          </dt>
          <dd className="mt-1 text-ink">
            {lastEventAt === null ? (
              <span className="text-ink-subtle">Nenhum evento aceito ainda</span>
            ) : (
              <>
                {lastEventType === null ? 'Evento' : EVENT_LABEL[lastEventType]} <span className="text-ink-subtle">· {formatDateTime(lastEventAt)}</span>
              </>
            )}
          </dd>
        </div>
      </dl>

      {semSegredo ? (
        <p className="flex items-start gap-2 rounded-lg border border-status-warning/50 bg-status-warning/5 px-3 py-2 text-xs text-ink-muted">
          <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-warning" />
          <span>
            Sem o segredo, o motor responde <span className="font-mono text-ink">401</span> a todo evento desta origem e nada entra na régua — e a plataforma não avisa. Peça à
            administração para configurá-lo.
          </span>
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" icon="raio" onClick={abrirTeste} disabled={!connected} title={connected ? undefined : 'Conecte a plataforma antes de testar'}>
          Testar webhook
        </Button>
        <span role="status">
          {ultimoTeste !== null ? (
            <Badge tone={ultimoTeste.status === 'accepted' ? 'good' : 'critical'} icon={ultimoTeste.status === 'accepted' ? 'check' : 'x'}>
              Último teste: {ultimoTeste.status === 'accepted' ? '202 · caminho aberto' : '401 · parou na assinatura'}
            </Badge>
          ) : null}
        </span>
      </div>

      <HistoricoDeEventos eventos={eventos} eventsLast7Days={integracao.eventsLast7Days} rejectedLast7Days={integracao.rejectedLast7Days} connected={connected} />

      <TesteDeWebhook open={testando} onClose={() => setTestando(false)} integracao={integracao} sequencia={sequencia} onResultado={registrarResultado} />
    </Panel>
  );
}
